import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  message,
  Form,
  Input,
  Select,
  Alert,
} from 'antd';
import { validPhoneNumber } from '../../global/validation';
import ModalWrapper from './ModalWrapper';

const { TextArea } = Input;

const renderTemplatePreview = (template, patientName, referringProvider) => {
  if (!template?.initial_greeting) return '';

  return template.initial_greeting
    .replace(/\{\{\s*patient_name\s*\}\}/g, patientName || '')
    .replace(/\{\{\s*referring_provider\s*\}\}/g, referringProvider || '')
    .replace(/\{\{\s*caller_intro\s*\}\}/g, template.caller_intro || '');
};

class MairaCallConfirm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      receiverType: this.props.modalType.data.receiverType,
      patientName: this.props.modalType.data.patientName,
      patientPhoneNumber: this.props.modalType.data.patientPhoneNumber,
      referringProvider: this.props.modalType.data.referringProvider,
      callTemplates: this.props.modalType.data.callTemplates || [],
      selectedTemplateId: this.props.modalType.data.selectedTemplateId,
      mairaCallFeatureEnabled: this.props.modalType.data.mairaCallFeature,
      submitting: false,
    };
  }

  send = async () => {
    // Guard against double-invocation: even though the modal now wires Queue Call
    // through a single path, this state check prevents a duplicate queue if the
    // button is somehow clicked twice before the async request returns.
    if (this.state.submitting) return;

    const {
      hideModal,
      modalType: { data: { sendToken } },
      triggerMairaCall,
    } = this.props;
    const {
      receiverType,
      patientName,
      patientPhoneNumber,
      referringProvider,
      selectedTemplateId,
    } = this.state;

    this.setState({ submitting: true });

    const result = await triggerMairaCall({
      sendToken,
      receiverType,
      templateId: selectedTemplateId,
      patientName,
      patientPhoneNumber,
      referringProvider,
    });

    this.setState({ submitting: false });

    if (result?.error) {
      if (result?.response?.status === 409) {
        const cooldownExpiresAt = result?.response?.data?.cooldown_expires_at;
        message.error({
          content: this.cooldownErrorMessage(cooldownExpiresAt),
        });
      } else {
        message.error({
          content: result?.response?.data?.error || 'Unable to queue MAIRA call.',
        });
      }
      return;
    }

    hideModal();
    message.success({
      content: 'MAIRA call queued.',
    });
  }

  handleTemplateChange = (value) => {
    this.setState({ selectedTemplateId: value });
  }

  cooldownErrorMessage = (cooldownExpiresAt) => {
    if (!cooldownExpiresAt) {
      return 'A referral management call was already queued for this patient recently. Try again later.';
    }

    const cooldownDate = new Date(cooldownExpiresAt);
    if (Number.isNaN(cooldownDate.getTime())) {
      return 'A referral management call was already queued for this patient recently. Try again later.';
    }

    return `A referral management call was already queued for this patient recently. Try again after ${cooldownDate.toLocaleString()}.`;
  }

  render() {
    const { hideModal } = this.props;
    const {
      patientName,
      patientPhoneNumber,
      referringProvider,
      callTemplates,
      selectedTemplateId,
      mairaCallFeatureEnabled,
      submitting,
    } = this.state;

    const selectedTemplate = callTemplates.find((template) => template.id === selectedTemplateId) || null;
    const disableSubmitButton = (
      !mairaCallFeatureEnabled
      || !patientName
      || !selectedTemplateId
      || !patientPhoneNumber
      || !validPhoneNumber(patientPhoneNumber)
      || callTemplates.length === 0
      || submitting
    );

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={{ width: '320px' }}
        action={this.send}
        actionName="Queue Call"
        dismiss={submitting ? '' : 'Cancel'}
        disabled={disableSubmitButton}
        modalTitle="Queue MAIRA Call"
        isDraggable
      >
        {/* Intentionally omit form="mairaCall" / htmlType="submit" on ModalWrapper:
            ModalWrapper already wires the footer button to `action={this.send}`
            via onClick. Wiring it as a form submit button as well would invoke
            send twice (form.onFinish + button.onClick) per Queue Call press. */}
        <Form layout="vertical">
          {callTemplates.length === 0 && (
            <div style={{ marginBottom: '25px' }}>
              <Alert
                message="No MAIRA call templates are available. Ask your admin to configure one first."
                type="warning"
                showIcon
              />
            </div>
          )}

          <p><i>Please confirm the information below is correct</i></p>

          <Form.Item label="Call Template" required>
            <Select
              value={selectedTemplateId}
              onChange={this.handleTemplateChange}
              options={callTemplates.map((template) => ({
                value: template.id,
                label: template.name,
              }))}
              placeholder="Select a call template"
            />
          </Form.Item>

          <Form.Item label="Patient Name" required>
            <Input
              value={patientName}
              onChange={(e) => this.setState({ patientName: e.target.value })}
              placeholder="Enter patient name"
            />
          </Form.Item>

          <Form.Item label="Patient Phone Number" required>
            <Input
              value={patientPhoneNumber}
              onChange={(e) => this.setState({ patientPhoneNumber: e.target.value })}
              placeholder="Enter phone number"
            />
          </Form.Item>

          <Form.Item label="Referring Provider">
            <Input
              value={referringProvider}
              onChange={(e) => this.setState({ referringProvider: e.target.value })}
              placeholder="Enter referring provider"
            />
          </Form.Item>

          <Form.Item label="Greeting Preview">
            <TextArea
              value={renderTemplatePreview(selectedTemplate, patientName, referringProvider)}
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled
            />
          </Form.Item>
        </Form>
      </ModalWrapper>
    );
  }
}

MairaCallConfirm.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.shape({
    data: PropTypes.shape({
      sendToken: PropTypes.string.isRequired,
      receiverType: PropTypes.string,
      patientName: PropTypes.string,
      patientPhoneNumber: PropTypes.string,
      referringProvider: PropTypes.string,
      callTemplates: PropTypes.array,
      selectedTemplateId: PropTypes.number,
      mairaCallFeature: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  triggerMairaCall: PropTypes.func.isRequired,
};

export default MairaCallConfirm;
